import React, { useEffect, useContext, useState } from 'react';
import { AjaxContext, AppContext, ModalContext, formatListData } from 'commons-ui';
import { isNil } from 'lodash';

import { PAGE_NAMES, MISCELLANEOUS_RESOURCES } from 'constant';
import { SelectFilterContent } from '../submodules/selectFilter';
import { InvoiceFilters, INVOICE_TYPE, GAME_NAME_OPTIONS, GAME_NAMES } from '../constants';
import {
	columnConfigs,
	columnConfigsTax,
	columnConfigsAck,
	columnConfigsCredit,
	columnConfigsRefund,
	columnConfigQuizTax,
	columnConfigsPostDeposit
} from '../submodules/list/listConfig';

const useService = () => {
	const { postData } = useContext(AjaxContext);
	const {
		app: { appName },
		showAlert
	} = useContext(AppContext);
	const { setModalConfig } = useContext(ModalContext);
	const [showLoader, setShowLoader] = useState<boolean>(false);
	const [invoiceData, setInvoiceData] = useState<any>({ data: [], rowCount: 0 });
	const [pageNumber, setPageNumber] = useState<any>({ currPage: 0, nextPage: null });
	const [filters, setFilters] = useState<InvoiceFilters | undefined>(undefined);
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);

	const handleFilterFormClose = () => {
		setModalConfig({ isOpen: false });
	};

	const fetchAllInvoiceData = async (jumpPageNumber: number) => {
		try {
			setShowLoader(true);
			let requestBody: any = {
				gameName: filters?.gameName,
				pageNumber: jumpPageNumber,
				limit: 50,
				invoiceType: filters?.invoiceType,
				uid: filters?.userId,
				tournamentId: filters?.tournamentID
			};
			if (!isNil(filters?.dateRangeFrom) && !isNil(filters?.dateRangeTill)) {
				requestBody = {
					...requestBody,
					dateFilter: {
						startTime: filters?.dateRangeFrom,
						endTime: filters?.dateRangeTill
					}
				};
			}
			const response: any = await postData({
				endpoint: `/dashboard/fetchBulkInvoice/${PAGE_NAMES.miscellaneous}-${MISCELLANEOUS_RESOURCES.invoiceGeneration}-fetchBulkInvoice`,
				queryParams: {
					appName
				},
				body: requestBody
			});
			setInvoiceData({ rowCount: response?.data?.data?.data?.length, data: formatListData(response?.data?.data?.data) });
			setPageNumber({ currPage: jumpPageNumber, nextPage: response?.data?.data?.nextPageNumber });
			setForceUpdate(true);
		} catch (error: any) {
			setInvoiceData({ data: [], rowCount: 0 });
			setPageNumber({ currPage: 0, nextPage: null });
			setForceUpdate(true);
			showAlert('Error while getting invoice data!', { type: 'error' });
		} finally {
			setShowLoader(false);
		}
	};

	const handleFilterSubmit = (submittedFilters: InvoiceFilters) => {
		const { tournamentID, dateRangeFrom, dateRangeTill, gameName, userId, invoiceType } = submittedFilters;
		setFilters({ tournamentID, dateRangeFrom, dateRangeTill, gameName, userId, invoiceType });
	};

	const handleFilterModalOpen = () => {
		setModalConfig({
			isOpen: true,
			header: 'Invoice Filter',
			content: <SelectFilterContent handleFilterSubmit={handleFilterSubmit} onClose={handleFilterFormClose} />
		});
	};

	const getAllInvoiceData = () => {
		setForceUpdate(false);
		return invoiceData;
	};

	const jumpBack = async () => {
		if (pageNumber.currPage > 0) {
			await fetchAllInvoiceData(pageNumber.currPage - 1);
		}
	};

	const jumpNext = async () => {
		if (pageNumber.nextPage !== null) {
			await fetchAllInvoiceData(pageNumber.nextPage);
		}
	};

	useEffect(() => {
		handleFilterModalOpen();
	}, []);

	useEffect(() => {
		if (filters !== null && filters !== undefined) {
			fetchAllInvoiceData(0);
		}
	}, [filters]);

	const selectColumnConfigs = () => {
		const invoiceType: string | undefined = filters?.invoiceType;
		const gameName: string | undefined = filters?.gameName;
		if (gameName === GAME_NAME_OPTIONS[GAME_NAMES.QUIZ]) return columnConfigQuizTax;
		switch (invoiceType) {
			case INVOICE_TYPE.TAX_INVOICE: {
				return columnConfigsTax;
			}
			case INVOICE_TYPE.ACKNOWLEDGMENT_SLIP: {
				return columnConfigsAck;
			}
			case INVOICE_TYPE.CREDIT_NOTE: {
				return columnConfigsCredit;
			}
			case INVOICE_TYPE.REFUND_RECEIPT: {
				return columnConfigsRefund;
			}
			case INVOICE_TYPE.DEPOSIT_TAX_INVOICE: {
				return columnConfigsPostDeposit;
			}
			default: {
				return columnConfigs;
			}
		}
	};

	return {
		getAllInvoiceData,
		showLoader,
		fetchAllInvoiceData,
		handleFilterModalOpen,
		filters,
		forceUpdate,
		pageNumber,
		jumpBack,
		jumpNext,
		setShowLoader,
		selectColumnConfigs
	};
};

export default useService;
